import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { PlaneTakeoff } from "lucide-react";

// Airport type definition
interface Airport {
  name: string;
  code: string;
  city: string;
  country: string;
}

interface SimpleAirportInputProps {
  id: string;
  placeholder: string;
  className?: string;
  defaultValue?: string;
  onSelect?: (airport: { name: string; code: string; fullName: string }) => void;
}

/**
 * A simplified airport input component with autocomplete functionality
 * that doesn't rely on external APIs or UI libraries
 */
export default function SimpleAirportInput({
  id,
  placeholder,
  className = "",
  defaultValue = "",
  onSelect
}: SimpleAirportInputProps) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Airport[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // List of major airports worldwide
  const AIRPORTS: Airport[] = [
    // Major US Airports
    { name: "Hartsfield-Jackson Atlanta International Airport", code: "ATL", city: "Atlanta", country: "USA" },
    { name: "Los Angeles International Airport", code: "LAX", city: "Los Angeles", country: "USA" },
    { name: "Chicago O'Hare International Airport", code: "ORD", city: "Chicago", country: "USA" },
    { name: "Dallas/Fort Worth International Airport", code: "DFW", city: "Dallas", country: "USA" },
    { name: "Denver International Airport", code: "DEN", city: "Denver", country: "USA" },
    { name: "San Francisco International Airport", code: "SFO", city: "San Francisco", country: "USA" },
    { name: "New York John F. Kennedy International Airport", code: "JFK", city: "New York", country: "USA" },
    { name: "Miami International Airport", code: "MIA", city: "Miami", country: "USA" },
    { name: "Seattle-Tacoma International Airport", code: "SEA", city: "Seattle", country: "USA" },
    { name: "Newark Liberty International Airport", code: "EWR", city: "Newark", country: "USA" },
    { name: "Phoenix Sky Harbor International Airport", code: "PHX", city: "Phoenix", country: "USA" },
    { name: "San Diego International Airport", code: "SAN", city: "San Diego", country: "USA" },
    { name: "Washington Dulles International Airport", code: "IAD", city: "Washington DC", country: "USA" },
    { name: "Boston Logan International Airport", code: "BOS", city: "Boston", country: "USA" },
    { name: "Honolulu International Airport", code: "HNL", city: "Honolulu", country: "USA" },
    { name: "Las Vegas McCarran International Airport", code: "LAS", city: "Las Vegas", country: "USA" },
    { name: "Tampa International Airport", code: "TPA", city: "Tampa", country: "USA" },
    { name: "Austin-Bergstrom International Airport", code: "AUS", city: "Austin", country: "USA" },
    { name: "Portland International Airport", code: "PDX", city: "Portland", country: "USA" },
    { name: "LaGuardia Airport", code: "LGA", city: "New York", country: "USA" },
    { name: "Salt Lake City International Airport", code: "SLC", city: "Salt Lake City", country: "USA" },
    { name: "Detroit Metropolitan Airport", code: "DTW", city: "Detroit", country: "USA" },
    { name: "Minneapolis-Saint Paul International Airport", code: "MSP", city: "Minneapolis", country: "USA" },
    { name: "Philadelphia International Airport", code: "PHL", city: "Philadelphia", country: "USA" },
    { name: "Charlotte Douglas International Airport", code: "CLT", city: "Charlotte", country: "USA" },
    { name: "Nashville International Airport", code: "BNA", city: "Nashville", country: "USA" },
    { name: "St. Louis Lambert International Airport", code: "STL", city: "St. Louis", country: "USA" },
    { name: "Raleigh-Durham International Airport", code: "RDU", city: "Raleigh", country: "USA" },
    { name: "Chicago Midway International Airport", code: "MDW", city: "Chicago", country: "USA" },
    { name: "Fort Lauderdale-Hollywood International Airport", code: "FLL", city: "Fort Lauderdale", country: "USA" },
    { name: "San Antonio International Airport", code: "SAT", city: "San Antonio", country: "USA" },
    { name: "Kansas City International Airport", code: "MCI", city: "Kansas City", country: "USA" },
    { name: "New Orleans International Airport", code: "MSY", city: "New Orleans", country: "USA" },
    { name: "Sacramento International Airport", code: "SMF", city: "Sacramento", country: "USA" },
    { name: "Pittsburgh International Airport", code: "PIT", city: "Pittsburgh", country: "USA" },
    { name: "Cleveland Hopkins International Airport", code: "CLE", city: "Cleveland", country: "USA" },
    { name: "Oakland International Airport", code: "OAK", city: "Oakland", country: "USA" },
    { name: "San Jose International Airport", code: "SJC", city: "San Jose", country: "USA" },
    { name: "John Wayne Airport", code: "SNA", city: "Orange County", country: "USA" },
    { name: "Cincinnati/Northern Kentucky International Airport", code: "CVG", city: "Cincinnati", country: "USA" },
    
    // Major Canadian Airports
    { name: "Toronto Pearson International Airport", code: "YYZ", city: "Toronto", country: "Canada" },
    { name: "Vancouver International Airport", code: "YVR", city: "Vancouver", country: "Canada" },
    { name: "Montreal-Trudeau International Airport", code: "YUL", city: "Montreal", country: "Canada" },
    { name: "Calgary International Airport", code: "YYC", city: "Calgary", country: "Canada" },
    { name: "Edmonton International Airport", code: "YEG", city: "Edmonton", country: "Canada" },
    { name: "Ottawa Macdonald-Cartier International Airport", code: "YOW", city: "Ottawa", country: "Canada" },
    { name: "Halifax Stanfield International Airport", code: "YHZ", city: "Halifax", country: "Canada" },
    { name: "Winnipeg James Armstrong Richardson International Airport", code: "YWG", city: "Winnipeg", country: "Canada" },
    
    // Major European Airports
    { name: "London Heathrow Airport", code: "LHR", city: "London", country: "UK" },
    { name: "Paris Charles de Gaulle Airport", code: "CDG", city: "Paris", country: "France" },
    { name: "Amsterdam Airport Schiphol", code: "AMS", city: "Amsterdam", country: "Netherlands" },
    { name: "Frankfurt Airport", code: "FRA", city: "Frankfurt", country: "Germany" },
    { name: "Istanbul Airport", code: "IST", city: "Istanbul", country: "Turkey" },
    { name: "Barcelona–El Prat Airport", code: "BCN", city: "Barcelona", country: "Spain" },
    { name: "Munich Airport", code: "MUC", city: "Munich", country: "Germany" },
    { name: "Madrid Barajas Airport", code: "MAD", city: "Madrid", country: "Spain" },
    { name: "Brussels Airport", code: "BRU", city: "Brussels", country: "Belgium" },
    { name: "Vienna International Airport", code: "VIE", city: "Vienna", country: "Austria" },
    { name: "Rome Fiumicino Airport", code: "FCO", city: "Rome", country: "Italy" },
    { name: "Zurich Airport", code: "ZRH", city: "Zurich", country: "Switzerland" },
    { name: "Berlin Brandenburg Airport", code: "BER", city: "Berlin", country: "Germany" },
    { name: "Copenhagen Airport", code: "CPH", city: "Copenhagen", country: "Denmark" },
    { name: "Dublin Airport", code: "DUB", city: "Dublin", country: "Ireland" },
    { name: "Helsinki Airport", code: "HEL", city: "Helsinki", country: "Finland" },
    { name: "Milan Malpensa Airport", code: "MXP", city: "Milan", country: "Italy" },
    { name: "Oslo Airport", code: "OSL", city: "Oslo", country: "Norway" },
    { name: "Stockholm Arlanda Airport", code: "ARN", city: "Stockholm", country: "Sweden" },
    { name: "Athens International Airport", code: "ATH", city: "Athens", country: "Greece" },
    { name: "Lisbon Portela Airport", code: "LIS", city: "Lisbon", country: "Portugal" },
    { name: "London Gatwick Airport", code: "LGW", city: "London", country: "UK" },
    { name: "Geneva Airport", code: "GVA", city: "Geneva", country: "Switzerland" },
    { name: "Nice Côte d'Azur Airport", code: "NCE", city: "Nice", country: "France" },
    { name: "Manchester Airport", code: "MAN", city: "Manchester", country: "UK" },
    { name: "Prague Václav Havel Airport", code: "PRG", city: "Prague", country: "Czech Republic" },
    { name: "Warsaw Chopin Airport", code: "WAW", city: "Warsaw", country: "Poland" },
    { name: "Budapest Ferenc Liszt International Airport", code: "BUD", city: "Budapest", country: "Hungary" },
    { name: "Lyon–Saint-Exupéry Airport", code: "LYS", city: "Lyon", country: "France" },
    
    // Major Asian Airports
    { name: "Beijing Capital International Airport", code: "PEK", city: "Beijing", country: "China" },
    { name: "Tokyo Haneda Airport", code: "HND", city: "Tokyo", country: "Japan" },
    { name: "Shanghai Pudong International Airport", code: "PVG", city: "Shanghai", country: "China" },
    { name: "Hong Kong International Airport", code: "HKG", city: "Hong Kong", country: "China" },
    { name: "Tokyo Narita International Airport", code: "NRT", city: "Tokyo", country: "Japan" },
    { name: "Seoul Incheon International Airport", code: "ICN", city: "Seoul", country: "South Korea" },
    { name: "Singapore Changi Airport", code: "SIN", city: "Singapore", country: "Singapore" },
    { name: "Bangkok Suvarnabhumi Airport", code: "BKK", city: "Bangkok", country: "Thailand" },
    { name: "Kuala Lumpur International Airport", code: "KUL", city: "Kuala Lumpur", country: "Malaysia" },
    { name: "Jakarta Soekarno–Hatta International Airport", code: "CGK", city: "Jakarta", country: "Indonesia" },
    { name: "Taipei Taoyuan International Airport", code: "TPE", city: "Taipei", country: "Taiwan" },
    { name: "Mumbai Chhatrapati Shivaji International Airport", code: "BOM", city: "Mumbai", country: "India" },
    { name: "Delhi Indira Gandhi International Airport", code: "DEL", city: "Delhi", country: "India" },
    { name: "Osaka Kansai International Airport", code: "KIX", city: "Osaka", country: "Japan" },
    { name: "Manila Ninoy Aquino International Airport", code: "MNL", city: "Manila", country: "Philippines" },
    
    // Middle East Airports
    { name: "Dubai International Airport", code: "DXB", city: "Dubai", country: "UAE" },
    { name: "Doha Hamad International Airport", code: "DOH", city: "Doha", country: "Qatar" },
    { name: "Abu Dhabi International Airport", code: "AUH", city: "Abu Dhabi", country: "UAE" },
    { name: "Tel Aviv Ben Gurion Airport", code: "TLV", city: "Tel Aviv", country: "Israel" },
    { name: "Riyadh King Khalid International Airport", code: "RUH", city: "Riyadh", country: "Saudi Arabia" },
    { name: "Jeddah King Abdulaziz International Airport", code: "JED", city: "Jeddah", country: "Saudi Arabia" },
    
    // Australian & Oceanian Airports
    { name: "Sydney Airport", code: "SYD", city: "Sydney", country: "Australia" },
    { name: "Melbourne Airport", code: "MEL", city: "Melbourne", country: "Australia" },
    { name: "Brisbane Airport", code: "BNE", city: "Brisbane", country: "Australia" },
    { name: "Perth Airport", code: "PER", city: "Perth", country: "Australia" },
    { name: "Auckland Airport", code: "AKL", city: "Auckland", country: "New Zealand" },
    { name: "Wellington International Airport", code: "WLG", city: "Wellington", country: "New Zealand" },
    
    // South American Airports
    { name: "São Paulo–Guarulhos International Airport", code: "GRU", city: "São Paulo", country: "Brazil" },
    { name: "Rio de Janeiro–Galeão International Airport", code: "GIG", city: "Rio de Janeiro", country: "Brazil" },
    { name: "Buenos Aires Ministro Pistarini International Airport", code: "EZE", city: "Buenos Aires", country: "Argentina" },
    { name: "Santiago de Chile International Airport", code: "SCL", city: "Santiago", country: "Chile" },
    { name: "Lima Jorge Chávez International Airport", code: "LIM", city: "Lima", country: "Peru" },
    { name: "Bogotá El Dorado International Airport", code: "BOG", city: "Bogotá", country: "Colombia" },
    
    // African Airports
    { name: "Johannesburg O. R. Tambo International Airport", code: "JNB", city: "Johannesburg", country: "South Africa" },
    { name: "Cairo International Airport", code: "CAI", city: "Cairo", country: "Egypt" },
    { name: "Cape Town International Airport", code: "CPT", city: "Cape Town", country: "South Africa" },
    { name: "Casablanca Mohammed V International Airport", code: "CMN", city: "Casablanca", country: "Morocco" },
    { name: "Nairobi Jomo Kenyatta International Airport", code: "NBO", city: "Nairobi", country: "Kenya" },
    { name: "Addis Ababa Bole International Airport", code: "ADD", city: "Addis Ababa", country: "Ethiopia" }
  ];

  // Function to filter airports based on search text
  const getSuggestions = (searchText: string) => {
    if (!searchText) return [];
    
    const normalizedSearch = searchText.toLowerCase();
    return AIRPORTS.filter(airport => 
      airport.city.toLowerCase().includes(normalizedSearch) ||
      airport.name.toLowerCase().includes(normalizedSearch) ||
      airport.code.toLowerCase().includes(normalizedSearch)
    ).slice(0, 7); // Limit to 7 results for better UX
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    const newSuggestions = getSuggestions(value);
    setSuggestions(newSuggestions);
    setShowSuggestions(newSuggestions.length > 0);
  };

  // Handle selection of an airport
  const handleSelectAirport = (airport: Airport) => {
    setInputValue(`${airport.city} (${airport.code})`);
    setShowSuggestions(false);
    
    if (onSelect) {
      onSelect({
        name: airport.name,
        code: airport.code,
        fullName: `${airport.city} (${airport.code})`
      });
    }
  };

  // Click outside handler to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        id={id}
        className={className}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (inputValue) {
            setSuggestions(getSuggestions(inputValue));
            setShowSuggestions(suggestions.length > 0);
          }
        }}
      />
      
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-md max-h-60 overflow-auto"
        >
          {suggestions.map((airport) => (
            <div
              key={airport.code}
              className="py-2 px-3 hover:bg-accent cursor-pointer flex items-center"
              onClick={() => handleSelectAirport(airport)}
            >
              <PlaneTakeoff className="mr-2 h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-medium">{airport.city} ({airport.code})</div>
                <div className="text-xs text-muted-foreground truncate">{airport.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}